import { useState, useEffect } from "react";
import useApi from "@/hooks/useApi";

interface AdminFeatures {
  enableQuickStartWizards: boolean;
  enableMcp: boolean;
  maxMcpServers: number;
  /** Max MCP tool calls per single conversation (prevents runaway loops). Default: 20. */
  maxMcpCallsPerConversation: number;
  /** Monthly MCP tool call budget across all conversations. Default: 1000. */
  maxMcpCallsPerMonth: number;
  /** Enables AI-to-AI delegation and section jumps in workflow AI nodes. */
  enableOrchestration: boolean;
  /** Enables Page Widgets — lightweight slide-out widgets on customer pages. */
  enablePageWidgets: boolean;
  /** Native HubSpot CRM integration — contact search + transcript push. */
  enableHubSpot: boolean;
  /** Native BigCommerce e-commerce integration — customer/order/product search. */
  enableBigCommerce: boolean;
  /** Native Microsoft Dynamics 365 CRM integration — contact/account/lead + rules engine. */
  enableDynamics: boolean;
  /** Maximum deployments allowed for this site. Default: 1. */
  maxDeployments: number;
  /** Current number of active (non-deleted) deployments. */
  currentDeployments: number;
  /** WebRTC P2P video chat for agents and visitors. */
  enableVideoChat: boolean;
  /** OneDrive/SharePoint file attachment sharing in agent chat conversations. Requires M365 connected. */
  enableOneDriveAttachments: boolean;
  /** Microsoft Teams channel notifications via incoming webhooks (resolve, new chat, missed, SLA breach). */
  enableTeamsNotifications: boolean;
  /** Slack channel notifications via incoming webhooks (resolve, new chat, missed, SLA breach). */
  enableSlackNotifications: boolean;
  /** Outlook Calendar booking + Calendly integration. Default: true (all plans). */
  enableCalendarBooking: boolean;
  /** Native Magento / Adobe Commerce e-commerce integration. */
  enableMagento: boolean;
  /** Shopify native integration — order lookup, product search, cart recovery, customer profiles. */
  enableShopify: boolean;
  /** WooCommerce native integration — product search, order lookup, customer history, stock checking. */
  enableWooCommerce: boolean;
  /** Uplisting vacation rental integration — listing lookup, reservation search, guest profiles. */
  enableUplisting: boolean;
  /** Guesty property management integration — listing lookup, reservation search, guest profiles. */
  enableGuesty: boolean;
  /** Cloudbeds hospitality integration — property info, guest lookup, reservation management. */
  enableCloudbeds: boolean;
  /** Native Velaro CRM — deals, contacts, companies, activities, buying signals, and 24 AI tools. */
  enableNativeCrm: boolean;
  /** Proactive Engagement Engine — page-aware contextual messaging powered by unified knowledge index. */
  enableProactiveEngagement: boolean;
  /** Pipedrive CRM integration — contacts, deals, organizations, activities, notes, pipeline. */
  enablePipedrive: boolean;
  /** QuickBooks Online integration — customers, invoices, payments, estimates, reporting. */
  enableQuickBooks: boolean;
  /** Zoho CRM integration — contacts, leads, deals, accounts, activities, pipeline reporting. */
  enableZoho: boolean;
  /** Toast POS integration — menu, orders, tables, hours, restaurant info. */
  enableToast: boolean;
  /** Mindbody integration — classes, bookings, members, staff, pricing. */
  enableMindbody: boolean;
  /** Monday.com integration — boards, items, subitems, updates, columns, lead capture. */
  enableMonday: boolean;
  /** Jobber field service management — job tracking, quotes, invoices, client lookup, service requests. */
  enableJobber: boolean;
  /** NetSuite ERP integration — customer lookup, sales orders, invoices, inventory, CRM records. */
  enableNetsuite: boolean;
  /** Salesforce Sales Cloud + Service Cloud — contact/account/lead/case/opportunity AI tools. */
  enableSalesforce: boolean;
  /** Klaviyo email/SMS marketing automation — profile lookup, list management, event tracking. */
  enableKlaviyo: boolean;
  /** Accela civic platform — permits, licenses, inspections, service requests. */
  enableAccela: boolean;
  /** Formstack forms integration — form lookup, submission retrieval, field mapping. */
  enableFormstack: boolean;
  /** Square Appointments + Retail — availability, booking, catalog, orders, loyalty. Default: true. */
  enableSquare: boolean;
  /** Clover POS integration — item search, order lookup, customer profiles, store info. */
  enableClover: boolean;
  /** Microsoft Power Automate webhook trigger integration. */
  enablePowerAutomate: boolean;
  /** Make.com (formerly Integromat) webhook trigger integration. */
  enableMake: boolean;
  /** Zapier webhook trigger integration. */
  enableZapier: boolean;
  /** Bring-Your-Own-Storage compliance transcript archiving to Azure Blob or S3. */
  enableTranscriptStorage: boolean;
  /** ServiceNow ITSM — incident, request, change, knowledge base AI tools. */
  enableServiceNow: boolean;
  /** Recruiting bot — job listing lookup, application status, HR intake workflows. */
  enableRecruitingBot: boolean;
  /** Microsoft Bookings AI skills — list services, check availability, create/lookup appointments. */
  enableMicrosoftBookings: boolean;
  /** SecureForms integration — secure form submissions via webhook callbacks. */
  enableSecureForms: boolean;
  /** VAST Retail (Kerridge CS) tire shop DMS — customer, vehicle, tire inventory, appointments, work orders. */
  enableVast: boolean;
  /** Nextiva VoIP CTI — inbound call screen pop, click-to-call, call log sync. */
  enableNextiva: boolean;
  /**
   * Ecommerce Sync — Klaviyo-competitive: persistent shopper profiles, purchase-based segmentation,
   * triggered campaign enrollment, and revenue attribution. Powers the Shoppers tab on all ecommerce
   * integration pages. Kill switch: set Subscription.EnableEcommerceSync=false in DB.
   */
  enableEcommerceSync: boolean;
  /** Gmail inbox integration — connect Gmail/Workspace mailboxes for inbound email ticketing. */
  enableGmailInbox: boolean;
  /** Gmail Domain-Wide Delegation — enterprise add-on; lets Workspace admins add org mailboxes without per-user OAuth. */
  enableGmailDwd: boolean;
  /** Outlook inbox integration — connect Microsoft 365 mailboxes for inbound email ticketing. */
  enableOutlookInbox: boolean;
  /** SAP ERP AI tools — Business One (session) + S/4HANA (OAuth, future). */
  enableSapSkills: boolean;
}

const DEFAULT_FEATURES: AdminFeatures = {
  enableQuickStartWizards: true,
  enableMcp: true,
  maxMcpServers: 3,
  maxMcpCallsPerConversation: 20,
  maxMcpCallsPerMonth: 1000,
  enableOrchestration: false,
  enablePageWidgets: false,
  enableHubSpot: false,
  enableBigCommerce: false,
  enableDynamics: false,
  maxDeployments: 1,
  currentDeployments: 0,
  enableVideoChat: false,
  enableOneDriveAttachments: false,
  enableTeamsNotifications: false,
  enableSlackNotifications: false,
  enableCalendarBooking: true,
  enableMagento: false,
  enableShopify: false,
  enableWooCommerce: false,
  enableUplisting: false,
  enableGuesty: false,
  enableCloudbeds: false,
  enableNativeCrm: false,
  enableProactiveEngagement: false,
  enablePipedrive: false,
  enableQuickBooks: false,
  enableZoho: false,
  enableToast: false,
  enableMindbody: false,
  enableMonday: false,
  enableJobber: false,
  enableNetsuite: false,
  enableSalesforce: false,
  enableKlaviyo: false,
  enableAccela: false,
  enableFormstack: false,
  enableSquare: true,   // DB default is true — keep legacy sites unlocked
  enableClover: false,
  enablePowerAutomate: false,
  enableMake: false,
  enableZapier: false,
  enableTranscriptStorage: false,
  enableServiceNow: false,
  enableRecruitingBot: false,
  enableMicrosoftBookings: false,
  enableVast: false,
  enableNextiva: false,
  enableSecureForms: false,
  enableEcommerceSync: false,
  enableGmailInbox: false,
  enableGmailDwd: false,
  enableOutlookInbox: false,
  enableSapSkills: false,
};

/**
 * Returns v20-specific feature flags from the admin backend.
 * These are distinct from the v10 subscription flags in useSubscriptions().
 * loading=true while the first fetch is in-flight; gates should render null
 * instead of "not enabled" during that window.
 */
export default function useAdminFeatures(): AdminFeatures & { loading: boolean } {
  const api = useApi();
  const [features, setFeatures] = useState<AdminFeatures>(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.admin
      .get("Features")
      .then((res) => res.json())
      .then((data) => {
        setFeatures({
          enableQuickStartWizards: data?.enableQuickStartWizards ?? true,
          enableMcp: data?.enableMcp ?? true,
          maxMcpServers: data?.maxMcpServers ?? 3,
          maxMcpCallsPerConversation: data?.maxMcpCallsPerConversation ?? 20,
          maxMcpCallsPerMonth: data?.maxMcpCallsPerMonth ?? 1000,
          enableOrchestration: data?.enableOrchestration ?? false,
          enablePageWidgets: data?.enablePageWidgets ?? false,
          enableHubSpot: data?.enableHubSpot ?? false,
          enableBigCommerce: data?.enableBigCommerce ?? false,
          enableDynamics: data?.enableDynamics ?? false,
          maxDeployments: data?.maxDeployments ?? 1,
          currentDeployments: data?.currentDeployments ?? 0,
          enableVideoChat: data?.enableVideoChat ?? false,
          enableOneDriveAttachments: data?.enableOneDriveAttachments ?? false,
          enableTeamsNotifications: data?.enableTeamsNotifications ?? false,
          enableSlackNotifications: data?.enableSlackNotifications ?? false,
          enableCalendarBooking: data?.enableCalendarBooking ?? true,
          enableMagento: data?.enableMagento ?? false,
          enableShopify: data?.enableShopify ?? false,
          enableWooCommerce: data?.enableWooCommerce ?? false,
          enableUplisting: data?.enableUplisting ?? false,
          enableGuesty: data?.enableGuesty ?? false,
          enableCloudbeds: data?.enableCloudbeds ?? false,
          enableNativeCrm: data?.enableNativeCrm ?? false,
          enableProactiveEngagement: data?.enableProactiveEngagement ?? false,
          enablePipedrive: data?.enablePipedrive ?? false,
          enableQuickBooks: data?.enableQuickBooks ?? false,
          enableZoho: data?.enableZoho ?? false,
          enableToast: data?.enableToast ?? false,
          enableMindbody: data?.enableMindbody ?? false,
          enableMonday: data?.enableMonday ?? false,
          enableJobber: data?.enableJobber ?? false,
          enableNetsuite: data?.enableNetsuite ?? false,
          enableSalesforce: data?.enableSalesforce ?? false,
          enableKlaviyo: data?.enableKlaviyo ?? false,
          enableAccela: data?.enableAccela ?? false,
          enableFormstack: data?.enableFormstack ?? false,
          enableSquare: data?.enableSquare ?? true,
          enableClover: data?.enableClover ?? false,
          enablePowerAutomate: data?.enablePowerAutomate ?? false,
          enableMake: data?.enableMake ?? false,
          enableZapier: data?.enableZapier ?? false,
          enableTranscriptStorage: data?.enableTranscriptStorage ?? false,
          enableServiceNow: data?.enableServiceNow ?? false,
          enableRecruitingBot: data?.enableRecruitingBot ?? false,
          enableMicrosoftBookings: data?.enableMicrosoftBookings ?? false,
          enableSecureForms: data?.enableSecureForms ?? false,
          enableEcommerceSync: data?.enableEcommerceSync ?? false,
          enableVast: data?.enableVastSkills ?? false,
          enableNextiva: data?.enableNextiva ?? false,
          enableGmailInbox: data?.enableGmailInbox ?? false,
          enableGmailDwd: data?.enableGmailDwd ?? false,
          enableOutlookInbox: data?.enableOutlookInbox ?? false,
          enableSapSkills: data?.enableSapSkills ?? false,
        });
      })
      .catch(() => {
        // Non-fatal — keep defaults
      })
      .finally(() => {
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...features, loading };
}
